import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleFromIndexerStore } from '@/models/stores/vehicle-listings';
import type {
  VehicleFromIndexer,
  VehicleSeatingCapacity,
} from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { VehicleSeatingCapacityBadge } from './seating-capacity-badge';

export const getSeatingCapacityOptions = (): Option<string>[] => {
  const seatingIcon = <Users className="h-3 w-3" />;
  return [
    {
      value: '2',
      label: '2 seats',
      icon: seatingIcon,
      hotkey: '2',
    },
    {
      value: '4',
      label: '4 seats',
      icon: seatingIcon,
      hotkey: '4',
    },
    {
      value: '5',
      label: '5 seats',
      icon: seatingIcon,
      hotkey: '5',
    },
    {
      value: '6',
      label: '6 seats',
      icon: seatingIcon,
      hotkey: '6',
    },
    {
      value: '7',
      label: '7 seats',
      icon: seatingIcon,
      hotkey: '7',
    },
  ];
};

export const SEATING_CAPACITY_OPTIONS_MAP = getSeatingCapacityOptions();

export type VehicleSeatingCapacitySelectorProps = {
  vehicleId: string;
  value: VehicleFromIndexer['seating_capacity'];
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function VehicleSeatingCapacitySelector({
  vehicleId,
  value,
  hideCopy = false,
  side = 'left',
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: VehicleSeatingCapacitySelectorProps) {
  const seatingCapacityButtonRef = useRef<HTMLButtonElement>(null);

  const updateSeatingCapacityMutation = useMutation({
    onMutate: async ({ seatingCapacity }) => {
      vehicleFromIndexerStore.updateSeatingCapacity({
        id: vehicleId,
        seatingCapacity,
      });
    },
    mutationFn: async ({
      seatingCapacity,
    }: {
      seatingCapacity: VehicleFromIndexer['seating_capacity'];
    }) => {
      return await pdkAxios.post(
        `/inventory/vehicles/${vehicleId}/seating_capacity`,
        {
          seating_capacity: seatingCapacity,
        },
      );
    },
    onError: (error) => {
      toast({
        title: 'Error updating seating capacity',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSeatingCapacityChange = (
    newSeatingCapacity: VehicleFromIndexer['seating_capacity'],
  ) => {
    updateSeatingCapacityMutation.mutate({
      seatingCapacity: newSeatingCapacity,
    });
  };

  const currentOption = useMemo(() => {
    return SEATING_CAPACITY_OPTIONS_MAP.find(
      (option) => option.value === value?.toString(),
    );
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>{HOTKEYS.VEHICLE_SEATING_CAPACITY}</KeyboardKeyIcon>
        }
        placeholder="Set seating capacity..."
        currentOption={currentOption}
        onChange={(value) =>
          handleSeatingCapacityChange(parseInt(value) as VehicleSeatingCapacity)
        }
        emptyMessage="No seating capacity found"
        options={SEATING_CAPACITY_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = SEATING_CAPACITY_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleSeatingCapacityChange(
              parseInt(option.value) as VehicleSeatingCapacity,
            );
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div>
          <VehicleSeatingCapacityBadge
            value={value}
            hideCopy={hideCopy}
            buttonRef={seatingCapacityButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.VEHICLE_SEATING_CAPACITY}
          onHotkeyPressed={() => {
            seatingCapacityButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
